import { Link } from 'react-router-dom';
import LegalPage, { Section } from './components/LegalPage';
import { CONTACT_EMAIL } from './constants';

const LAST_UPDATED = 'August 24, 2026';

function Privacy() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated={LAST_UPDATED}>
      <Section title="1. Who is responsible">
        <p>
          Dissona (&ldquo;the Service&rdquo;) is operated as a personal project. The operator is the
          data controller for the personal data described here and can be reached at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="link hover:underline">
            {CONTACT_EMAIL}
          </a>
          . This policy explains what we collect, why, and what you can ask us to do about it. It
          complements our <Link to="/terms" className="link hover:underline">Terms of Service</Link>.
        </p>
      </Section>

      <Section title="2. What we collect">
        <p>
          <strong className="font-medium text-gray-800 dark:text-gray-200">Account data</strong> —
          your username and email address, and a hashed password. If you sign in with Google we
          receive your email address and basic profile information from Google instead; we never see
          your Google password.
        </p>
        <p>
          <strong className="font-medium text-gray-800 dark:text-gray-200">Content you upload</strong>{' '}
          — the documents you submit, the chapters extracted from them, and everything derived from
          them: generated audio, summaries and key points.
        </p>
        <p>
          <strong className="font-medium text-gray-800 dark:text-gray-200">Usage data</strong> —
          your playback position and which chapters you have played, your settings (voice,
          processing preferences), and the shares you issue or receive. Our servers keep ordinary
          technical logs, which include IP addresses, for security and debugging.
        </p>
      </Section>

      <Section title="3. Why we process it, and on what basis">
        <p>
          We process account data, your content and your usage data to provide the Service you asked
          for — converting your documents into audio, keeping your place, and sharing projects with
          the people you nominate. The legal basis is performance of our contract with you.
        </p>
        <p>
          We process technical logs to keep the Service secure and working, on the basis of our
          legitimate interest in operating it. Browser notifications, where you enable them, are
          based on your consent and can be switched off at any time.
        </p>
        <p>
          We do not use your data for advertising, we do not profile you, and we do not sell it.
        </p>
      </Section>

      <Section title="4. Cookies and local storage">
        <p>
          We use two cookies, both strictly necessary: <code>access_token</code>, which keeps you
          signed in, and <code>token_issued_at</code>, which records when that session began. We also
          store data locally in your browser — your playback position, cached key points, and
          notification preferences — and, briefly during a Google sign-in, a security token in
          session storage.
        </p>
        <p>
          None of this is used for tracking or analytics, and the Service embeds no third-party
          advertising or analytics scripts. Because all of it is required for functionality you have
          explicitly requested, no cookie consent banner is presented. Signing out clears the session
          cookies and the locally cached content.
        </p>
      </Section>

      <Section title="5. Who else sees your data">
        <p>
          We share personal data only with service providers who process it on our behalf: our
          hosting and storage provider, an email provider used to deliver share invitations and
          account messages, and — depending on how the Service is configured — text-to-speech and
          language-model providers that generate audio and summaries from the text you upload. These
          providers act on our instructions and may not use your data for their own purposes.
        </p>
        <p>
          Beyond that, your content is visible only to you and to the people you explicitly share a
          project with. We may disclose data where legally required to do so.
        </p>
      </Section>

      <Section title="6. How long we keep it">
        <p>
          We keep your account data and content for as long as your account exists. When you delete a
          project it is removed along with its generated audio; when you delete your account, your
          personal data and content are deleted. Technical logs are retained for a short period and
          then discarded. Backups may persist for a limited time after deletion.
        </p>
      </Section>

      <Section title="7. Your rights">
        <p>
          Under the GDPR you have the right to access your personal data, to have it corrected or
          erased, to restrict or object to its processing, and to receive it in a portable format.
          You can exercise most of these directly in the app — your settings page holds your account
          details, and deleting your account removes your data. For anything else, write to{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="link hover:underline">
            {CONTACT_EMAIL}
          </a>
          .
        </p>
        <p>
          If you believe we have handled your data improperly, you may lodge a complaint with your
          local supervisory authority — in Bulgaria, the Commission for Personal Data Protection.
        </p>
      </Section>

      <Section title="8. Security">
        <p>
          Data is transmitted over encrypted connections and access to it is restricted to what is
          needed to run the Service. No system is perfectly secure, however, and we cannot guarantee
          absolute security; please keep your password confidential and keep your own copies of
          anything you cannot afford to lose.
        </p>
      </Section>

      <Section title="9. Children">
        <p>
          The Service is not directed to children under 16, and we do not knowingly collect their
          personal data. If you believe a child has provided us with personal data, please contact us
          and we will delete it.
        </p>
      </Section>

      <Section title="10. Changes to this policy">
        <p>
          We may update this policy from time to time. The date at the top reflects the most recent
          revision; continued use of the Service after a change takes effect constitutes acceptance
          of the updated policy.
        </p>
      </Section>
    </LegalPage>
  );
}

export default Privacy;
